import { useMemo, useState } from 'react';

import { EyeIcon, GitBranchIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { FieldTypesOptions } from 'constants/field-types-enum';
import { textWithReferences } from 'helpers/form';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useWatch } from 'react-hook-form';
import { ReportingPreview } from 'views/Company/tabs/Branding/Reporting/ReportingPreview';

import { Checkbox, FormMessage, MessageType } from 'components/FormComponents';
import { ValidationAlert } from 'components/FormComponents';
import FadeInMotion from 'components/Motion/FadeInMotion';
import { StatusPill } from 'components/Pills/StatusPills';

const ACTION_BADGE_COLORS = {
  DEFAULT: 'bg-primary-4 text-white',
};

const isIncludedInCustomReport = field =>
  field?.in_custom_report !== false && field?.in_custom_report !== 0;

const parseQuestionOrderInput = input => {
  const trimmed = input.trim();
  if (!trimmed) {
    return { orders: new Set(), error: 'Enter at least one question number.' };
  }

  const orders = new Set();
  const parts = trimmed
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);

  for (const part of parts) {
    if (part.includes('-')) {
      const rangeParts = part.split('-').map(value => value.trim());
      if (rangeParts.length !== 2 || !rangeParts[0] || !rangeParts[1]) {
        return { orders: null, error: `Invalid range: ${part}` };
      }

      const start = Number(rangeParts[0]);
      const end = Number(rangeParts[1]);
      if (!Number.isInteger(start) || !Number.isInteger(end) || start < 1 || end < 1) {
        return { orders: null, error: `Invalid range: ${part}` };
      }

      const [from, to] = start <= end ? [start, end] : [end, start];
      for (let order = from; order <= to; order += 1) {
        orders.add(order);
      }
      continue;
    }

    const order = Number(part);
    if (!Number.isInteger(order) || order < 1) {
      return { orders: null, error: `Invalid question number: ${part}` };
    }
    orders.add(order);
  }

  return { orders, error: null };
};

const questionLabel = (text, textParams) => (
  <span
    dangerouslySetInnerHTML={{
      __html:
        textWithReferences(text, textParams, { previewMode: true }, false) ||
        '<span class="text-neutral-5">Untitled question</span>',
    }}
  />
);

/**
 * Tab view for editing custom report fields.
 */
const CustomReportTab = ({
  formRef,
  handleSubmit,
  handleSave,
  onError,
  fields,
  control,
  setValue,
  getLogicJumpsCount,
  textParams = {},
  sidebarCollapsed,
  defaultBrand,
}) => {
  const watchedFields = useWatch({ control, name: 'fields' }) ?? fields;
  const [questionNumbersInput, setQuestionNumbersInput] = useState('');
  const [selectionError, setSelectionError] = useState('');

  const selectableFields = useMemo(
    () => watchedFields?.filter(field => !field?.options?.intro) ?? [],
    [watchedFields],
  );

  const selectableOrders = useMemo(
    () => new Set(selectableFields.map(field => field?.order).filter(order => order != null)),
    [selectableFields],
  );

  const customReportFields = useMemo(
    () => selectableFields.filter(isIncludedInCustomReport),
    [selectableFields],
  );

  const previewQuestions = useMemo(
    () =>
      customReportFields.map(field => {
        const typeOption = FieldTypesOptions?.find(f => f.key === field?.type);
        return {
          order: field.order,
          text: field.text,
          type_label: typeOption?.title ?? field.type,
        };
      }),
    [customReportFields],
  );

  const allSelected =
    selectableFields.length > 0 && customReportFields.length === selectableFields.length;

  const setFieldsIncluded = included => {
    fields.forEach((field, index) => {
      if (field?.options?.intro) return;
      setValue(`fields.${index}.in_custom_report`, included, {
        shouldDirty: true,
        shouldTouch: true,
      });
    });
  };

  const setFieldsIncludedByOrder = selectedOrders => {
    fields.forEach((field, index) => {
      if (field?.options?.intro) return;
      const order = (watchedFields[index] ?? field)?.order;
      setValue(`fields.${index}.in_custom_report`, selectedOrders.has(order), {
        shouldDirty: true,
        shouldTouch: true,
      });
    });
  };

  const handleSelectAllToggle = checked => {
    setSelectionError('');
    if (!checked) {
      setQuestionNumbersInput('');
    }
    setFieldsIncluded(checked);
  };

  const handleApplyQuestionNumbers = () => {
    const { orders, error } = parseQuestionOrderInput(questionNumbersInput);
    if (error) {
      setSelectionError(error);
      return;
    }

    const unknownOrders = [...orders].filter(order => !selectableOrders.has(order));
    if (unknownOrders.length > 0) {
      setSelectionError(
        `Unknown question number${unknownOrders.length === 1 ? '' : 's'}: ${unknownOrders.join(', ')}`,
      );
      return;
    }

    setSelectionError('');
    setFieldsIncludedByOrder(orders);
  };

  const handleToggle = (index, checked) => {
    setValue(`fields.${index}.in_custom_report`, checked, {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const renderLogicJumpIcon = logicJumpsCount => (
    <HugeiconsIcon
      icon={GitBranchIcon}
      className="text-neutral-6"
      size="20"
      title={logicJumpsCount > 0 ? `${logicJumpsCount} logic jumps` : 'No logic jumps'}
    />
  );

  return (
    <>
      <form ref={formRef} onSubmit={handleSubmit(handleSave, onError)}>
        <div className="flex px-6 py-8 gap-8">
          <div
            className={cn(
              'flex flex-col gap-4',
              sidebarCollapsed
                ? 'min-[1140px]:flex-1 min-[1140px]:min-w-0 min-[1140px]:max-w-[calc(13rem+32%+3rem)]'
                : 'w-1/3',
              'max-[1140px]:flex-1 max-[1140px]:min-w-0',
            )}>
            <FormMessage
              message="Custom reports follow your questionnaire's logic jumps. Questions that are skipped due to logic rules will not be included in the report, even if selected. The preview is displayed using your company's default brand settings."
              type={MessageType.Info}
              closeable={false}
              noMargin
            />
            <div className="flex flex-col bg-neutral-1 border border-neutral-4 rounded-3xl shadow-sm overflow-hidden max-h-[70vh]">
              <div className="bg-neutral-1 border-b border-neutral-4 px-4 py-3 flex flex-col gap-2">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <Checkbox
                      checked={allSelected}
                      onChange={handleSelectAllToggle}
                      className="!py-0 !mb-0"
                      dataTestId="custom-report-select-all-checkbox"
                    />
                    <span className="text-neutral-7 text-xl whitespace-nowrap">Select all</span>
                    <span className="bg-primary-1 text-primary-4 text-lg font-medium rounded-full px-3 py-0.5 whitespace-nowrap">
                      {customReportFields.length} / {selectableFields.length}
                    </span>
                  </div>
                  <div className="flex flex-col items-end gap-1 min-w-0">
                    <div className="flex items-stretch max-w-full rounded-2xl border border-neutral-4 bg-neutral-1 p-2">
                      <div
                        className={cn(
                          'flex items-center gap-2 min-w-[10rem] flex-1',
                          selectionError ? 'border-red-2' : 'border-neutral-4',
                        )}>
                        <span className="text-primary-4 text-xl font-medium shrink-0">#</span>
                        <input
                          id="custom-report-question-numbers"
                          data-testid="custom-report-question-numbers-input"
                          type="text"
                          value={questionNumbersInput}
                          onChange={event => {
                            setQuestionNumbersInput(event.target.value);
                            if (selectionError) setSelectionError('');
                          }}
                          onBlur={() => {
                            if (!questionNumbersInput.trim()) {
                              setSelectionError('');
                              return;
                            }
                            const { error } = parseQuestionOrderInput(questionNumbersInput);
                            if (error) setSelectionError(error);
                          }}
                          onKeyDown={event => {
                            if (event.key === 'Enter') {
                              event.preventDefault();
                              handleApplyQuestionNumbers();
                            }
                          }}
                          placeholder="1, 3, 5-7"
                          className="min-w-0 w-full bg-transparent text-xl text-neutral-7 placeholder:text-neutral-5 outline-none"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleApplyQuestionNumbers}
                        data-testid="custom-report-apply-question-numbers-button"
                        className="bg-button-primary hover:bg-button-primary-hover text-text-contrast text-xl font-medium px-5 py-2 rounded-2xl border border-button-primary shrink-0 transition-colors">
                        Apply
                      </button>
                    </div>
                  </div>
                </div>
                {selectionError && (
                  <FadeInMotion key="selection-error">
                    <ValidationAlert
                      show={true}
                      message={selectionError}
                      className="!p-2 text-xl"
                    />
                  </FadeInMotion>
                )}
              </div>
              <div className="flex flex-col overflow-y-auto p-4 bg-neutral-2 gap-4">
                {fields.map((field, index) => {
                  if (field?.options?.intro) return null;
                  const watchedField = watchedFields[index] ?? field;
                  const logicJumpsCount = getLogicJumpsCount?.(watchedField?.order) ?? 0;
                  const typeOption = FieldTypesOptions?.find(f => f.key === watchedField?.type);
                  const typeTitle = typeOption?.title ?? watchedField?.type ?? '';

                  return (
                    <div
                      key={field.key ?? index}
                      data-testid={`custom-report-question-${watchedField?.order}`}
                      className="bg-neutral-1 border border-neutral-4 rounded-2xl shadow-sm">
                      <div className="p-4 w-full flex items-center justify-between">
                        <div className="flex items-center gap-2 min-w-0">
                          <Checkbox
                            checked={isIncludedInCustomReport(watchedField)}
                            onChange={checked => handleToggle(index, checked)}
                            className="!py-0 !mb-0"
                            dataTestId={`custom-report-question-checkbox-${watchedField?.order}`}
                          />
                          <span className="text-xl font-medium bg-primary-1 text-primary-4 rounded-full w-10 h-10 flex items-center justify-center shrink-0">
                            {watchedField?.order}
                          </span>
                          <div className="flex items-center gap-1.5 min-w-0">
                            {typeOption?.icon != null && (
                              <HugeiconsIcon
                                icon={typeOption.icon}
                                size="16"
                                className="shrink-0 text-neutral-6"
                              />
                            )}
                            <span className="text-neutral-6 text-xl truncate">{typeTitle}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {!watchedField.required && (
                            <StatusPill
                              text="Optional"
                              status="light"
                              className="px-4 self-start text-sm font-semibold"
                            />
                          )}
                          <OverlayTrigger
                            trigger={['hover', 'focus']}
                            placement="top"
                            overlay={
                              <Tooltip>
                                {logicJumpsCount > 0
                                  ? `${logicJumpsCount} logic jumps`
                                  : 'No logic jumps'}
                              </Tooltip>
                            }>
                            <div className="relative">
                              {renderLogicJumpIcon(logicJumpsCount)}
                              {logicJumpsCount > 0 && (
                                <span
                                  className={cn(
                                    'absolute -top-1 -right-1 text-xs rounded-full w-5 h-5 flex items-center justify-center',
                                    ACTION_BADGE_COLORS.DEFAULT,
                                  )}>
                                  {logicJumpsCount}
                                </span>
                              )}
                            </div>
                          </OverlayTrigger>
                        </div>
                      </div>
                      <div className="px-4 pb-4 w-full">
                        <div className="flex gap-1.5 bg-neutral-2 border border-neutral-4 rounded-2xl px-3 py-2 overflow-hidden items-center text-neutral-7 text-xl min-w-0">
                          {questionLabel(watchedField.text, textParams)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </form>
      {customReportFields.length > 0 && (
        <div
          data-testid="custom-report-preview"
          className="fixed top-[calc(7.4rem+10rem+3rem)] right-6 bottom-6 left-[calc(18rem+32%+3rem)] max-[1140px]:left-[calc(7rem+46%)] z-20 cursor-not-allowed bg-yellow-2/30 p-4 rounded-3xl overflow-hidden shadow-sm border border-yellow-3/30 flex flex-col transition-[left] duration-200">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2 flex-shrink-0">
            <span className="text-xl text-orange-3 font-medium flex items-center gap-2">
              <HugeiconsIcon icon={EyeIcon} size="16" className="text-orange-3" /> Preview only -
              Not Interactive
            </span>
            <span className="text-lg text-orange-3 font-regular">
              This is a preview. Buttons and inputs do not work.
            </span>
          </div>
          <div className="flex-1 min-h-0 bg-neutral-1 border border-neutral-4 rounded-2xl shadow-sm overflow-y-auto mb-1">
            <ReportingPreview brand={defaultBrand} questions={previewQuestions} custom_report />
          </div>
        </div>
      )}
    </>
  );
};

export default CustomReportTab;
